import { formatLocalDateTime } from "./datetimeUtils";

const MUSCLE_ALIASES = {
  hamstring: "hamstrings",
  hamstrings: "hamstrings",
  ischio: "hamstrings",
  ischios: "hamstrings",
  quadriceps: "quadriceps",
  quad: "quadriceps",
  quads: "quadriceps",
  adductor: "adductors",
  adductors: "adductors",
  calf: "calf",
  calves: "calf",
  glute: "glutes",
  glutes: "glutes",
  gluteal: "glutes",
  ankle: "ankle_foot",
  foot: "ankle_foot",
  knee: "knee",
  hip: "hip",
  shin: "shin",
  thigh: "thigh",
  abs: "abs",
  back: "back",
  shoulder: "shoulder",
};

function asObject(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

function cleanString(value) {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  return text.length > 0 ? text : null;
}

export function humanizeAlertToken(value) {
  const text = cleanString(value);
  if (!text) return null;
  return text
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

export function normalizeMuscleKey(value) {
  const text = cleanString(value);
  if (!text) return null;

  const normalized = text.toLowerCase().replace(/[_\s-]+/g, "");
  if (MUSCLE_ALIASES[normalized]) return MUSCLE_ALIASES[normalized];

  if (normalized.includes("hamstring") || normalized.includes("ischio")) return "hamstrings";
  if (normalized.includes("quad")) return "quadriceps";
  if (normalized.includes("adductor")) return "adductors";
  if (normalized.includes("calf") || normalized.includes("mollet")) return "calf";
  if (normalized.includes("glute")) return "glutes";
  if (normalized.includes("ankle") || normalized.includes("foot")) return "ankle_foot";
  if (normalized.includes("knee")) return "knee";
  if (normalized.includes("hip")) return "hip";
  if (normalized.includes("shin")) return "shin";
  if (normalized.includes("thigh")) return "thigh";
  if (normalized.includes("abs")) return "abs";
  if (normalized.includes("back")) return "back";
  if (normalized.includes("shoulder")) return "shoulder";

  return null;
}

export function extractAlertLocation(alert) {
  const payload = asObject(alert?.payload);
  const context = asObject(payload.context);
  const location = asObject(payload.location);
  const injury = asObject(payload.injury);

  const merged = {
    ...payload,
    ...location,
    ...injury,
    ...context,
  };

  return {
    muscle:
      cleanString(context.muscle) ||
      cleanString(context.body_part) ||
      cleanString(context.bodypart) ||
      cleanString(context.part) ||
      cleanString(location.muscle) ||
      cleanString(injury.muscle) ||
      cleanString(payload.muscle) ||
      cleanString(payload.body_part) ||
      cleanString(payload.bodypart) ||
      cleanString(payload.part) ||
      cleanString(payload.zone) ||
      cleanString(payload.region) ||
      cleanString(payload.area) ||
      null,
    side:
      cleanString(context.side) ||
      cleanString(context.laterality) ||
      cleanString(location.side) ||
      cleanString(injury.side) ||
      cleanString(payload.side) ||
      cleanString(payload.laterality) ||
      null,
    type:
      cleanString(context.injury_type) ||
      cleanString(context.issue_type) ||
      cleanString(context.type) ||
      cleanString(location.injury_type) ||
      cleanString(location.issue_type) ||
      cleanString(injury.type) ||
      cleanString(injury.injury_type) ||
      cleanString(payload.injury_type) ||
      cleanString(payload.issue_type) ||
      cleanString(payload.problem_type) ||
      null,
    where:
      cleanString(context.where) ||
      cleanString(location.where) ||
      cleanString(payload.where) ||
      cleanString(merged.location_name) ||
      null,
  };
}

function toValidDate(value) {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isFinite(value.getTime()) ? value : null;
  }
  const parsed = new Date(value);
  return Number.isFinite(parsed.getTime()) ? parsed : null;
}

export function getAlertReferenceDate(alertOrDateLike) {
  if (alertOrDateLike && typeof alertOrDateLike === "object" && !(alertOrDateLike instanceof Date)) {
    return toValidDate(alertOrDateLike.occurred_at) || toValidDate(alertOrDateLike.created_at);
  }
  return toValidDate(alertOrDateLike);
}

export function formatAlertAbsoluteDate(dateLike) {
  return formatLocalDateTime(dateLike) || null;
}

export function formatAlertSince(dateLike, locale = "en") {
  const date = getAlertReferenceDate(dateLike);
  if (!date) return null;

  const now = Date.now();
  const diffSeconds = Math.round((date.getTime() - now) / 1000);
  const absSeconds = Math.abs(diffSeconds);

  let value = diffSeconds;
  let unit = "second";

  if (absSeconds >= 31557600) {
    unit = "year";
    value = Math.round(diffSeconds / 31557600);
  } else if (absSeconds >= 2629800) {
    unit = "month";
    value = Math.round(diffSeconds / 2629800);
  } else if (absSeconds >= 604800) {
    unit = "week";
    value = Math.round(diffSeconds / 604800);
  } else if (absSeconds >= 86400) {
    unit = "day";
    value = Math.round(diffSeconds / 86400);
  } else if (absSeconds >= 3600) {
    unit = "hour";
    value = Math.round(diffSeconds / 3600);
  } else if (absSeconds >= 60) {
    unit = "minute";
    value = Math.round(diffSeconds / 60);
  }

  try {
    const formatter = new Intl.RelativeTimeFormat(locale || "en", { numeric: "auto" });
    return formatter.format(value, unit);
  } catch {
    const absValue = Math.abs(value);
    const suffix = value < 0 ? "ago" : "from now";
    return `${absValue} ${unit}${absValue > 1 ? "s" : ""} ${suffix}`;
  }
}

export function resolveAlertPlayerName(alert, fallback = "Unknown player") {
  const first = cleanString(alert?.player?.first_name) || "";
  const last = cleanString(alert?.player?.last_name) || "";
  const full = `${first} ${last}`.trim();
  return full || fallback;
}

export const RPE_AUTO_CLOSE_ALERT_TYPE = "rpe_auto_close";

export function isClubLevelAlert(alert) {
  const type = String(alert?.alert_type || "");
  if (type === RPE_AUTO_CLOSE_ALERT_TYPE) return true;
  return !alert?.player_id && !alert?.player?.id;
}

export function getAlertDisplayCopy(alert, t) {
  const type = String(alert?.alert_type || "");
  if (type === RPE_AUTO_CLOSE_ALERT_TYPE) {
    const payload = asObject(alert?.payload);
    const names = Array.isArray(payload.missing_player_names)
      ? payload.missing_player_names.map((name) => cleanString(name)).filter(Boolean)
      : [];
    const label = cleanString(payload.session_label) || t("rpe.session_label", { defaultValue: "RPE" });
    const hours = Number.isFinite(Number(payload.hours)) ? Number(payload.hours) : 24;
    const value = payload.coach_rpe_value == null || payload.coach_rpe_value === ""
      ? "—"
      : String(payload.coach_rpe_value);
    const title = t("alerts_center.rpe_auto_close_title", {
      label,
      defaultValue: alert?.title || "RPE session closed automatically",
    });
    const description = names.length
      ? t("alerts_center.rpe_auto_close_description", {
          count: names.length,
          names: names.join(", "),
          hours,
          value,
          label,
          defaultValue: alert?.description,
        })
      : t("alerts_center.rpe_auto_close_description_all_answered", {
          hours,
          label,
          defaultValue: alert?.description,
        });
    return {
      title,
      description,
      typeLabel: t("alerts_center.rpe_auto_close_type", { defaultValue: "RPE" }),
      subjectLabel: t("alerts_center.rpe_auto_close_subject", { defaultValue: "Club" }),
    };
  }

  return {
    title: alert?.title || t("alerts_center.alert_fallback_title", { defaultValue: "Alert" }),
    description: alert?.description || t("alerts_center.no_description", { defaultValue: "No description" }),
    typeLabel: humanizeAlertToken(alert?.alert_type) || t("alerts_center.alert_fallback_title", { defaultValue: "Alert" }),
    subjectLabel: resolveAlertPlayerName(
      alert,
      t("player_risk_alert.player_unknown", { defaultValue: "Unknown player" })
    ),
  };
}

export function resolveAlertPlayerAvatar(alert) {
  const raw = cleanString(alert?.player?.profile_picture);
  if (!raw) return null;
  if (raw.startsWith("data:image/")) return raw;
  return `data:image/jpeg;base64,${raw}`;
}
